// Mirror suggestion prompts — the 30-question bank shown as one-tap chips on
// every /mirror entry (gated by the per-user "show_mirror_suggestions" toggle).
// Each key resolves via i18n to a concrete question the Mirror answers directly
// through its tools (agenda / tasks / focus / fitness / nutrition / sleep /
// habits / goals / language / check-in / brain).
//
// Rotation contract:
//   - show 3 per entry;
//   - advance a persisted cursor through a shuffled permutation of all 30, so
//     consecutive entries never repeat a prompt within a full pass;
//   - reshuffle ONLY when the 30-pool is exhausted, and guarantee the fresh
//     pass's first trio is not the same set as the previous entry's trio;
//   - cursor + order persist in localStorage (per-device is acceptable).

export const MIRROR_PROMPT_KEYS: readonly string[] = Array.from(
  { length: 30 },
  (_, i) => `mirror.prompts.p${i + 1}`,
);

const N = MIRROR_PROMPT_KEYS.length; // 30
const TRIO = 3;

const ORDER_KEY = "vyv_mirror_prompt_order";
const CURSOR_KEY = "vyv_mirror_prompt_cursor";
const LAST_KEY = "vyv_mirror_prompt_last";

// Fisher-Yates over [0, N). Math.random is fine here — this is browser runtime,
// not a workflow script.
function shuffled(): number[] {
  const a = Array.from({ length: N }, (_, i) => i);
  for (let i = N - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function sameSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length || a.length === 0) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

// Read the persisted order only if it is a valid permutation of 0..N-1 (guards
// against a stale localStorage payload from an older prompt-count).
function readOrder(): number[] | null {
  try {
    const raw = localStorage.getItem(ORDER_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length !== N) return null;
    const s = new Set(arr);
    if (s.size !== N) return null;
    for (let i = 0; i < N; i++) if (!s.has(i)) return null;
    return arr as number[];
  } catch {
    return null;
  }
}

function readLast(): number[] {
  try {
    const raw = localStorage.getItem(LAST_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as number[]) : [];
  } catch {
    return [];
  }
}

/**
 * Returns 3 prompt i18n keys for this /mirror entry and advances the persisted
 * cursor. Call once per mount (guard the caller so a strict-mode double invoke
 * doesn't skip a trio). Degrades gracefully if localStorage is unavailable
 * (private mode): returns a fresh random trio without persisting.
 */
export function nextSuggestionTrio(): string[] {
  let order = readOrder();
  const last = readLast();
  let cursor = 0;
  try {
    cursor = parseInt(localStorage.getItem(CURSOR_KEY) ?? "0", 10);
    if (!Number.isFinite(cursor) || cursor < 0) cursor = 0;
  } catch {
    cursor = 0;
  }

  if (!order) {
    order = shuffled();
    cursor = 0;
  }

  // Pool exhausted → reshuffle for a fresh pass; never repeat the previous trio.
  if (cursor + TRIO > N) {
    let next = shuffled();
    for (let tries = 0; tries < 8 && sameSet(next.slice(0, TRIO), last); tries++) {
      next = shuffled();
    }
    order = next;
    cursor = 0;
  }

  const idx = [order[cursor], order[cursor + 1], order[cursor + 2]];
  const nextCursor = cursor + TRIO;

  try {
    localStorage.setItem(ORDER_KEY, JSON.stringify(order));
    localStorage.setItem(CURSOR_KEY, String(nextCursor));
    localStorage.setItem(LAST_KEY, JSON.stringify(idx));
  } catch {
    /* private mode / quota — the trio is still returned, just not persisted. */
  }

  return idx.map((i) => MIRROR_PROMPT_KEYS[i]);
}
